import { Text, Image, Box, TextLink, SpinnerFullPage } from '@updater/ui-uds';
import { PageTemplate } from 'components/page-template';
import { LeftAligned } from 'components/left-aligned';
import { InfoRow } from 'components/info-row';
import { NextPage } from 'next';
import { MovingAppBrandedHeader } from 'components/branded-header';

import { formatPhone } from 'utils/phone-number';
import {
  useGetPropertyContactQuery,
  useSiteEntityQuery,
} from '@updater/consumer-graph';
import { useInviteToken } from '../components/onboarding/common/hooks';
import { useTrackViewOnMount } from 'utils/use-track-on-mount';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { useGetDivisionId } from 'hooks/use-get-divisionId';

const PropertyContact: NextPage = () => {
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'consumer_app_header_menu',
    object: 'property_contact',
    pageName: 'property_contact',
  });
  const { divisionId, loading: divisionLoading } = useGetDivisionId();

  const {
    data: propertyData,
    loading: propertyDataLoading,
    error: propertyError,
  } = useGetPropertyContactQuery({
    variables: {
      divisionId: divisionId,
    },
    skip: !divisionId,
  });

  const inviteToken = useInviteToken();
  const { data: branding, loading: brandingLoading } = useSiteEntityQuery({
    variables: {
      input: {
        inviteToken,
      },
    },
  });

  useTrackViewOnMount(trackEvent);

  if (propertyDataLoading || brandingLoading || divisionLoading) {
    return (
      <>
        <MovingAppBrandedHeader />
        <LeftAligned>
          <Box width="100%">
            <SpinnerFullPage />
          </Box>
        </LeftAligned>
      </>
    );
  }

  if (propertyError) {
    return (
      <h3>
        {propertyError?.message ||
          'Something went wrong, please come back later'}
      </h3>
    );
  }

  const address = propertyData?.division?.address;
  const { street, city, state, postalCode, unit } = address || {};

  const property = branding?.siteBranding?.entityBrandings?.find(
    (e) => e?.entityId === divisionId
  );

  return (
    <PageTemplate title="Property Contact" backButtonPath="/">
      {property?.logo?.url && (
        <Box mt="m">
          <Image
            alt={`${property.name} logo`}
            key={property.logo.url}
            src={property.logo.url}
            maxWidth={200}
            maxHeight={100}
          />
        </Box>
      )}
      <InfoRow heading="Name">
        <Text variant="m">{property?.name}</Text>
      </InfoRow>

      <InfoRow heading="Address">
        <Text variant="m">
          {street}
          <br />
          {unit && (
            <>
              {unit} <br />
            </>
          )}
          {city}, {state}, {postalCode}
        </Text>
      </InfoRow>

      <InfoRow heading="Contact">
        {property?.phone && (
          <Box>
            <TextLink
              data-testid="property-phone"
              href={`tel:${property.phone}`}
              variant="m"
              onClick={() =>
                trackEvent({
                  verb: 'clicked',
                  object: 'property_contact_phone',
                })
              }
            >
              {formatPhone(property.phone)}
            </TextLink>
          </Box>
        )}
        {property?.email && (
          <Box>
            <TextLink
              data-testid="property-email"
              href={`mailto:${property.email}`}
              variant="m"
              onClick={() =>
                trackEvent({
                  verb: 'clicked',
                  object: 'property_contact_email',
                })
              }
            >
              {property.email}
            </TextLink>
          </Box>
        )}
      </InfoRow>
    </PageTemplate>
  );
};

export default PropertyContact;
